/**
 * 管端搜索（⌘K）的匹配与排序。纯函数，不碰 React / DOM，node --test 直接测。
 *
 * 搜两类东西：
 * - 页面：菜单里的每一项（含三级的玩法格页），用当前语言的名字、中文名、页 id、所在分组都能搜到
 * - 配置项：接口视角登记表（src/api-lens）里的约 400 项，每项记着在哪一页、页面上叫什么。
 *   运营记得的往往是「恢复时间」「偷取比例」这种字段名，不记得它在「骰子参数」还是「对抗与控骰」
 *
 * 当前代理用不上的页（另一套建筑体系、只有多人才有的页）也进索引，但标成 hidden 并带原因：
 * 「找不到」有一半是这一页根本不在这个代理的菜单里，告诉他为什么比搜不到强。
 */
export interface SearchDoc {
  /** page 菜单页 / field 页面里的配置项 / content 配置内容的一行（某一关、某张卡、某件商品） / player 玩家 */
  kind: "page" | "field" | "content" | "player";
  /** 唯一键：页面用页 id，配置项用登记 id */
  key: string;
  /** 打开哪一页 */
  page: string;
  /** 主标题：页名 / 配置项在页面上的叫法 */
  title: string;
  /** 位置：分组 / 页 / 页内分组 */
  path: string;
  /** 其他可搜文本：中文名、页 id、登记 id、页内分组 */
  alt: string[];
  /** 当前代理的菜单里没有这一页：原因，界面上照写并置灰 */
  hidden?: string;
  /** content：实体名（关卡 / 卡包 / 商品…），结果行左边的标签 */
  tag?: string;
  /**
   * content：到页上之后怎么认出这一行。先按 rowKey 找 DataTable 的行标识，
   * 找不到再找文字或输入框的值等于 match 里任一项的表格行
   */
  rowKey?: string;
  match?: string[];
  /** content：跳页前要先做的事（建筑页要先选中那一关） */
  go?: () => void;
  /** player：玩家 id 的 hex，选中后直接打开玩家详情 */
  hex?: string;
}

/** 小写、去空白、全角转半角：「ＳＰＩＮ 奖励」与「spin奖励」算同一个词 */
export function norm(s: string): string {
  return s
    .replace(/[！-～]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .toLowerCase()
    .replace(/[\s　·・/／|｜_-]+/g, "");
}

/** 按空白切词：多个词要全部命中（「雷神 比例」） */
export function tokens(q: string): string[] {
  return q.split(/[\s　]+/).map(norm).filter(Boolean);
}

/** needle 的字依次出现在 hay 里（不要求相连）：「骰上限」命中「骰次上限」 */
function subseq(hay: string, needle: string): boolean {
  let i = 0;
  for (const c of hay) if (c === needle[i] && ++i === needle.length) return true;
  return false;
}

/** 单个词对一段文本的得分：0 = 没命中 */
function hit(text: string, tk: string, base: number): number {
  if (!text) return 0;
  if (text === tk) return base;
  if (text.startsWith(tk)) return base * 0.8;
  if (text.includes(tk)) return base * 0.6;
  return 0;
}

export function score(doc: SearchDoc, tks: string[]): number {
  if (!tks.length) return 0;
  const title = norm(doc.title);
  const path = norm(doc.path);
  const alt = doc.alt.map(norm);
  let sum = 0;
  for (const tk of tks) {
    const best = Math.max(
      hit(title, tk, 100),
      ...alt.map((a) => hit(a, tk, 50)),
      // 路径只算包含：搜「棋盘」时棋盘配置下的页都该出来，但排在名字里就有「棋盘」的后面
      path.includes(tk) ? 25 : 0,
      // 不相连的字只在主标题上认，且至少两个字——一个字的子序列几乎什么都命中
      tk.length >= 2 && subseq(title, tk) ? 15 : 0,
    );
    if (!best) return 0;
    sum += best;
  }
  if (doc.kind === "page") sum += 5;
  if (doc.hidden) sum -= 60;
  return Math.max(sum, 1);
}

export function search(docs: readonly SearchDoc[], q: string, limit = 60): SearchDoc[] {
  const tks = tokens(q);
  if (!tks.length) return [];
  return docs
    .map((d) => ({ d, s: score(d, tks) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.d.title.length - b.d.title.length)
    .slice(0, limit)
    .map((x) => x.d);
}

/** 标题里命中的片段，界面用来加粗：返回 [文本, 是否命中] 段落 */
export function highlight(title: string, q: string): [string, boolean][] {
  const tks = tokens(q);
  const marks = new Array<boolean>(title.length).fill(false);
  const lower = title.toLowerCase();
  for (const tk of tks) {
    const i = lower.indexOf(tk);
    if (i >= 0) for (let k = i; k < i + tk.length; k++) marks[k] = true;
  }
  const out: [string, boolean][] = [];
  for (let i = 0; i < title.length; i++) {
    const last = out[out.length - 1];
    if (last && last[1] === marks[i]) last[0] += title[i];
    else out.push([title[i], marks[i]]);
  }
  return out;
}
